import {
  Box,
  forwardRef,
  MenuButton,
  MenuItem,
  MenuList,
} from '@chakra-ui/react';
import type {
  MenuButtonProps,
  MenuItemProps,
  MenuListProps,
} from '@chakra-ui/react';
import { ChevronDown } from '@kula-ai/icons-core';
import { colors } from '@/theme/foundations/colors';

// Chakra ports of the @kula-ai/components Editor toolbar select primitives
// (`ToolbarSelectTrigger` / `ToolbarDropdown` / `ToolbarMenuItem`) so the
// in-repo FormEditor font controls read identically to the kula Email editor.
// core-web has no Tailwind, so kula's utility classes are translated to Chakra
// props + `theme/foundations/colors` tokens here.

// Soft layered dropdown shadow from the kula toolbar (Figma Frame 427319529):
// four stacked rgba(215,218,235) layers rather than Chakra's default menu shadow.
const MENU_SHADOW =
  '0px 4px 10px rgba(215,218,235,0.1), 0px 18px 18px rgba(215,218,235,0.09), 0px 40px 24px rgba(215,218,235,0.05), 0px 71px 29px rgba(215,218,235,0.01)';

// Trigger chrome: 32px tall, transparent until hover (secondary-25) / open
// (secondary-100, flagged by Chakra as `aria-expanded`), rounded.
const triggerBaseProps: MenuButtonProps = {
  as: Box,
  role: 'button',
  display: 'inline-flex',
  alignItems: 'center',
  h: '32px',
  px: '8px',
  borderRadius: '6px',
  cursor: 'pointer',
  bg: 'transparent',
  color: colors['secondary-700'],
  fontSize: '14px',
  lineHeight: '1',
  _hover: { bg: colors['secondary-25'], color: colors['primary-900'] },
  _expanded: { bg: colors['secondary-100'], color: colors['primary-900'] },
};

// kula toolbar select trigger: the control content plus the trailing chevron
// every kula picker renders, so it reads as a dropdown rather than a bare icon
// button. The inner flex row keeps the chevron to the right of the content
// regardless of the button's own display. `ChevronDown` is sized via `size`
// (icons-core ignores width/height). Forwards ref/props so it can sit inside a
// Chakra `Tooltip` + `Menu`.
export const ToolbarSelectTrigger = forwardRef<MenuButtonProps, 'button'>(
  ({ children, ...props }, ref) => (
    <MenuButton ref={ref} {...triggerBaseProps} {...props}>
      <Box
        as="span"
        display="flex"
        w="100%"
        alignItems="center"
        justifyContent="space-between"
        gap="4px"
      >
        {children}
        <ChevronDown size={16} color={colors['secondary-700']} />
      </Box>
    </MenuButton>
  )
);

// Dropdown surface: white, secondary-100 hairline border + soft shadow, no
// padding, options hairline-divided (kula's `divide-y divide-secondary-100`).
export const ToolbarSelectMenuList = ({
  children,
  ...props
}: MenuListProps) => (
  <MenuList
    minW="auto"
    maxH="180px"
    overflowY="auto"
    p="0"
    border="1px solid"
    borderColor={colors['secondary-100']}
    borderRadius="6px"
    boxShadow={MENU_SHADOW}
    zIndex={99999}
    sx={{
      '& [role="menuitem"] + [role="menuitem"]': {
        borderTop: '1px solid',
        borderColor: colors['secondary-100'],
      },
    }}
    {...props}
  >
    {children}
  </MenuList>
);

type ToolbarSelectMenuItemProps = MenuItemProps & { isSelected?: boolean };

// Option row: secondary-700 text; the selected row sits on secondary-25 and
// deepens to secondary-50 on hover, unselected rows stay transparent until a
// secondary-25 hover — matching the kula Select `isSelected` styling.
export const ToolbarSelectMenuItem = ({
  isSelected,
  ...props
}: ToolbarSelectMenuItemProps) => (
  <MenuItem
    fontSize="14px"
    px="8px"
    py="6px"
    borderRadius="0"
    color={colors['secondary-700']}
    bg={isSelected ? colors['secondary-25'] : 'transparent'}
    _hover={{
      bg: isSelected ? colors['secondary-50'] : colors['secondary-25'],
      color: colors['secondary-700'],
    }}
    _focus={{
      bg: isSelected ? colors['secondary-50'] : colors['secondary-25'],
    }}
    {...props}
  />
);
